/**
 * @typedef {Object} CursorTimePluginParams
 * @property {?boolean} deferInit Set to true to stop auto init in `addPlugin()`
 * @property {boolean} hideOnBlur=true Hide the cursor when the mouse leaves the
 * waveform
 * @property {number} zIndex=3 The z-index of the cursor element
 * @property {object} customStyle An object with custom styles which are applied
 * to the cursor element
 */

/**
 * Displays a block containing the current time at the position of the cursor on the waveform.
 *
 * @implements {PluginClass}
 * @extends {Observer}
 * @example
 * // es6
 * import CursorTimePlugin from 'wavesurfer.cursor.js';
 *
 * // commonjs
 * var CursorTimePlugin = require('wavesurfer.cursor.js');
 *
 * // if you are using <script> tags
 * var CursorTimePlugin = window.WaveSurfer.cursor;
 *
 * // ... initialising wavesurfer with the plugin
 * var wavesurfer = WaveSurfer.create({
 *   // wavesurfer options ...
 *   plugins: [
 *     CursorTimePlugin.create({
 *       // plugin options ...
 *     })
 *   ]
 * });
 */


class CursorTimePlugin {
  /**
   * Cursor plugin definition factory
   *
   * This function must be used to create a plugin definition which can be
   * used by wavesurfer to correctly instantiate the plugin.
   *
   * @param  {CursorTimePluginParams} params parameters use to initialise the
   * plugin
   * @return {PluginDefinition} an object representing the plugin
   * LS: stop Closure from swallowing static methods:
   * https://github.com/google/closure-compiler/issues/1776
   * @nocollapse
   */
  static create(params) {
    return {
      name: 'cursorTime',
      deferInit: params && params.deferInit ? params.deferInit : false,
      params: params,
      staticProps: {
        enableCursor() {
          console.warn('Deprecated enableCursor!');
          this.initPlugins('cursor');
        }
      },
      instance: CursorTimePlugin
    };
  }

  /**
   * Construct the plugin class. You probably want to use CursorTimePlugin.create
   * instead.
   *
   * @param {CursorTimePluginParams} params
   * @param {object} ws
   */
  constructor(params, ws) {
    /**
     * @type {CursorTimePluginParams}
     */
    this.defaultParams = {
      hideOnBlur: true,
      hideOnScroll: false,
      customStyle: {
        position: 'absolute',
        left: 0,
        bottom: '0',
        width: '50px',
        height: 'auto',
        padding: '5px',
        background: 'black',
        color: 'white',
        textAlign: 'center',
        opacity: '1',
      },
      zIndex: 4,
    };

    /** @private */
    this.wavesurfer = ws;
    /** @private */
    this.style = ws.util.style;
    /**
     * The cursor html element
     *
     * @type {?HTMLElement}
     */
    this.cursorTime = null;
    /** @private */
    this.params = Object.assign({}, this.defaultParams, params, {
      customStyle: Object.assign({}, this.defaultParams.customStyle, params.customStyle)
    });
  }

  fadeCursorTimeTo(opacity = 1) {
    this.cursorTime.style.opacity = opacity;
  }

  setDigits() {
    const duration = this.wavesurfer.getDuration();
    this.digits = 4; // 0:00 is 4 digits as it includes the separator

    if (duration >= 600 && duration < 3600) {
      this.digits = 5; // 00:00
    } else if (duration >= 3600) {
      this.digits = 7; // 0:00:00
    }

    return this;
  }

  appendCursorTime() {
    const style = Object.assign(
      this.params.customStyle,
      {
        zIndex: this.params.zIndex,
        display: 'block',
        opacity: this.params.hideOnBlur ? 0 : 1,
        boxSizing: 'border-box',
        transition: 'opacity 200ms ease-out',
        pointerEvents: 'none'
      });

    const element = document.createElement('span');
    element.appendChild(document.createTextNode(this.formatTime(this.time)));

    this.cursorTime = this.wavesurfer.container.appendChild(this.style(element, style));

    return this;
  }

  setTime(time) {
    this.time = time;

    return this;
  }

  formatTime(seconds) {
    const date = new Date(null);
    date.setSeconds(seconds);
    return date.toISOString().substr(19 - this.digits, this.digits);
  }

  calculateCenter(position, immediate) {
    const scrollLeft = this.wrapper.scrollLeft;
    const half = Math.floor(this.wrapper.clientWidth / 2);
    const maxScroll = this.wrapper.scrollWidth - this.wrapper.clientWidth;
    let target = position - half;
    let offset = target - scrollLeft;

    if (maxScroll === 0) {
      // no need to continue if scrollbar is not there
      return;
    }

    // if the cursor is currently visible...
    if (!immediate && -half <= offset && offset < half) {
      // we'll limit the "re-center" rate.
      const rate = 5;
      offset = Math.max(-rate, Math.min(rate, offset));
      target = scrollLeft + offset;
    }

    // limit target to valid range (0 to maxScroll)
    return Math.max(0, Math.min(maxScroll, target));
  }

  updateCursorTime() {
    // @todo Move a bunch of this so that it is only calculated once
    const time = this.wavesurfer.getCurrentTime();
    const wrapperWidth = this.wavesurfer.drawer.wrapper.offsetWidth;
    const progressOffset = this.wavesurfer.drawer.progressWave.offsetWidth;
    const cursorPosition = progressOffset - this.calculateCenter(progressOffset, false);
    const cursorTimeWidth = this.cursorTime.offsetWidth;
    const cursorTimeCenter = cursorTimeWidth / 2;
    const maxCursorTimeOffset = (wrapperWidth - cursorTimeWidth);

    let newPosition = 0;

    if (time !== this.time) {
      this.setTime(time);
      this.cursorTime.childNodes[0].nodeValue = this.formatTime(this.time);
    }

    if (cursorPosition > cursorTimeCenter) {
      newPosition = cursorPosition - cursorTimeCenter;
    }

    if (cursorPosition > (wrapperWidth - cursorTimeCenter)) {
      newPosition = maxCursorTimeOffset;
    }

    this.cursorTime.style.left = `${newPosition}px`;
  }

  showCursorTime() {
    this.fadeCursorTimeTo(1);
  }

  hideCursorTime() {
    this.fadeCursorTimeTo(0);
  }

  onMouseEnter() {
    this.showCursorTime();
  }

  onMouseLeave() {
    this.hideCursorTime();
  }

  onScroll() {
    if (this.cursorTime.style.opacity > 0) {
      setTimeout(() => this.showCursorTime(), 500);
    }

    this.hideCursorTime();
  }

  onReady() {
    this.setDigits().appendCursorTime();

    if (this.params.hideOnScroll) {
      this.wrapper.addEventListener('scroll', this.boundOnScroll);
    }

    if (this.params.hideOnBlur) {
      this.wrapper.addEventListener('mouseenter', this.boundOnMouseEnter);
      this.wrapper.addEventListener('mouseleave', this.boundOnMouseLeave);
    }
  }

  /**
   * Initialise the plugin (used by the Plugin API)
   */
  init() {
    this.wrapper = this.wavesurfer.container;
    this.cursorTime = this.wavesurfer.container.appendChild(
      document.createElement('div')
    );

    this.setTime(0);

    this.boundOnReady = () => this.onReady();
    this.boundShowCursorTime = () => this.showCursorTime();
    this.boundHideCursorTime = () => this.hideCursorTime();
    this.boundUpdateCursorTime = () => this.updateCursorTime();
    this.boundOnMouseEnter = () => this.onMouseEnter();
    this.boundOnMouseLeave = () => this.onMouseLeave();
    this.boundOnScroll = () => this.onScroll();

    this.wavesurfer.on('audioprocess', this.boundUpdateCursorTime);
    this.wavesurfer.on('pause', this.boundHideCursorTime);
    this.wavesurfer.on('play', this.boundShowCursorTime);
    this.wavesurfer.on('ready', this.boundOnReady);
    this.wavesurfer.on('seek', this.boundUpdateCursorTime);
  }

  /**
   * Destroy the plugin (used by the Plugin API)
   */
  destroy() {
    this.cursorTime.parentNode.removeChild(this.cursorTime);

    this.wavesurfer.un('audioprocess', this.boundUpdateCursorTime);
    this.wavesurfer.un('pause', this.boundHideCursorTime);
    this.wavesurfer.un('play', this.boundShowCursorTime);
    this.wavesurfer.un('ready', this.boundOnReady);
    this.wavesurfer.un('seek', this.boundUpdateCursorTime);

    this.wrapper.removeEventListener('mouseenter', this.boundOnMouseEnter);
    this.wrapper.removeEventListener('mouseleave', this.boundOnMouseLeave);
    this.wrapper.removeEventListener('scroll', this.boundOnScroll);
  }
}

export default CursorTimePlugin;
